/**
 * Card Component - Reusable card with consistent styling
 * Theme-based with support for structured content via subcomponents
 * Variants: default, green, transparent, elevated, outlined, feature
 */

import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import styles from './Card.module.css';

// Card Variants
export const CardVariants = {
  DEFAULT: 'default',
  GREEN: 'green',
  TRANSPARENT: 'transparent',
  ELEVATED: 'elevated',
  OUTLINED: 'outlined',
  FEATURE: 'feature'
};

const Card = ({
  children,
  variant = CardVariants.DEFAULT,
  className = '',
  padding,
  hover = true,
  level = 1, // Default to level 1 surface
  style = {},
  ...props
}) => {
  const { theme } = useTheme();

  // Map level to corresponding CSS variable
  const surfaceColor = `var(--surface-lvl${level})`;

  const cardClasses = [
    styles.card,
    styles[variant],
    hover ? styles.hover : styles.noHover,
    className
  ].filter(Boolean).join(' ');

  // Custom padding handling with theme support
  const customStyle = {
    ...style,
    background: variant === CardVariants.DEFAULT ? surfaceColor : undefined,
    ...(padding ? { '--card-padding': padding } : {})
  };

  return (
    <div
      className={cardClasses}
      style={customStyle}
      data-padding={padding ? '' : undefined}
      {...props}
    >
      <div className={styles.content}>
        {children}
      </div>
    </div>
  );
};

// Card Subcomponents for structured content
export const CardHeader = ({ children, className = '', style = {}, ...props }) => {
  const { theme } = useTheme();

  const headerStyles = {
    padding: `${theme.spacing.lg} ${theme.spacing.lg} ${theme.spacing.md}`,
    borderBottom: `1px solid ${theme.colors.border}`,
    ...style
  };

  return (
    <div
      className={`${styles.cardHeader} ${className}`}
      style={headerStyles}
      {...props}
    >
      {children}
    </div>
  );
};

export const CardTitle = ({ children, className = '', style = {}, as = 'h3', ...props }) => {
  const { theme } = useTheme();

  const titleStyles = {
    fontSize: theme.typography.sizes.lg,
    fontWeight: theme.typography.weights.semibold,
    color: theme.colors.textPrimary,
    margin: 0,
    ...style
  };

  const Component = as;

  return (
    <Component
      className={`${styles.cardTitle} ${className}`}
      style={titleStyles}
      {...props}
    >
      {children}
    </Component>
  );
};

export const CardDescription = ({ children, className = '', style = {}, ...props }) => {
  const { theme } = useTheme();

  const descStyles = {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    marginTop: theme.spacing.xs,
    ...style
  };

  return (
    <p
      className={`${styles.cardDescription} ${className}`}
      style={descStyles}
      {...props}
    >
      {children}
    </p>
  );
};

export const CardContent = ({ children, className = '', style = {}, noPadding = false, ...props }) => {
  const { theme } = useTheme();

  const contentStyles = noPadding ? style : {
    padding: theme.spacing.lg,
    ...style
  };

  return (
    <div
      className={`${styles.cardContent} ${className}`}
      style={contentStyles}
      {...props}
    >
      {children}
    </div>
  );
};

export const CardFooter = ({ children, className = '', style = {}, ...props }) => {
  const { theme } = useTheme();

  const footerStyles = {
    padding: `${theme.spacing.md} ${theme.spacing.lg} ${theme.spacing.lg}`,
    borderTop: `1px solid ${theme.colors.border}`,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.md,
    ...style
  };

  return (
    <div
      className={`${styles.cardFooter} ${className}`}
      style={footerStyles}
      {...props}
    >
      {children}
    </div>
  );
};

export default Card;
